import { useEffect, useState } from 'react';

import { H_TAG_NAMES } from '@/constant/common';
import { Predicate } from '@/types/common';
import { UpdateAnchorFn } from '@/types/navigation';
import { equals, flow, head, isEmpty, isNotEmpty, toLowerCase } from '@/utils/common';
import { triggerReplaceHistoryStateEvent } from '@/utils/customEvents';

import { useObserver } from '../useObserver';
import { useTimeout } from '../useTimeout';

const getHChildElements = (element: Element) =>
  Array.from(element.children).filter(child => H_TAG_NAMES.includes(toLowerCase(child.tagName)));

const applyObserverToElementPred = (element: Element) => {
  const isSection = (el: Element) => equals(toLowerCase(el.tagName), 'section');
  if (Array.from(element.children).some(isSection)) return false;

  return flow(element, [getHChildElements, isNotEmpty]);
};

const transformFn = (element: Element) => head(getHChildElements(element)) as Element;

export const useAutoUpdateActiveTOC = (externalUpdatePred: Predicate<number[]>, updateAnchor: UpdateAnchorFn) => {
  const timeoutedCallEvent = useTimeout(triggerReplaceHistoryStateEvent, 250);
  const [elements, setElements] = useState<NodeListOf<Element>>([] as unknown as NodeListOf<Element>);

  useEffect(() => {
    setElements(document.querySelectorAll('section[id]'));
  }, []);

  useObserver({
    shouldSkip: isEmpty(elements),
    observableElements: elements,
    onObserve: entry => {
      const entryElement = entry.target;
      const parentElementId = entryElement.parentElement?.id;

      if (parentElementId && externalUpdatePred(Date.now())) {
        updateAnchor(parentElementId);
        timeoutedCallEvent({ initiator: 'hook' });
      }
    },
    beforeApplyObserver: {
      predicate: applyObserverToElementPred,
      transform: transformFn,
    },
    observerConfig: {
      threshold: 0,
      rootMargin: '-10% 0px -80% 0px',
    },
  });
};
